import { Info, OctagonAlert, TriangleAlert, X } from "lucide-react";
import { Button } from "@marekl/adminer/components";
import { useAuth } from "../../auth/useAuth";
import { useMyNotices, type NoticeSeverity } from "../../lib/queries/notices";
import { NoticeText } from "./NoticeText";
import { DISMISSED_PREFIX, useStoredIds } from "./useStoredIds";
import styles from "./NoticeStrip.module.css";

const ICONS: Record<NoticeSeverity, typeof Info> = {
  info: Info,
  warn: TriangleAlert,
  err: OctagonAlert,
};

/**
 * The signed-in user's lišty: full-width strips directly under the top
 * bar, one per notice, the way `ImpersonationBanner` sits above it.
 *
 * Rendered by `AppTopbar`, because every page renders its own top bar and
 * the strip has to follow it. That also means it remounts on navigation,
 * so a × is kept in sessionStorage (merged, never overwritten: the popup in
 * `SignedInNotices` writes the same key). Popups live in `AppLayout`.
 */
export function NoticeStrip() {
  const { user } = useAuth();
  const { data: notices = [] } = useMyNotices();
  const dismissed = useStoredIds("session", `${DISMISSED_PREFIX}${user?.id ?? ""}`);
  // A × closed earlier no longer hides a lišta the admin has since made
  // non-dismissible: there is no way to bring it back otherwise.
  const banners = notices.filter(
    (n) => n.display === "banner" && !(n.dismissible && dismissed.ids.has(n.id)),
  );
  if (!user || banners.length === 0) return null;

  return (
    <div className={styles.stack}>
      {banners.map((n) => {
        const Icon = ICONS[n.severity];
        return (
          <div
            key={n.id}
            className={`${styles.strip} ${styles[n.severity]}`}
            role="status"
            aria-live="polite"
            data-testid="notice-strip"
          >
            <Icon className={styles.icon} size={16} aria-hidden="true" />
            <div className={styles.content}>
              <strong className={styles.title}>{n.title}</strong>
              <NoticeText text={n.body} />
            </div>
            {n.dismissible && (
              <Button
                iconOnly
                weight="ghost"
                size="sm"
                aria-label="Skrýt oznámení"
                title="Skrýt oznámení"
                startIcon={<X size={16} aria-hidden="true" />}
                onClick={() => dismissed.add(n.id)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
